import { graphql, Link, navigate, useStaticQuery } from 'gatsby';
import React, { FC, useContext, useMemo, useRef, useState, useEffect } from 'react';
import styled, { css } from 'styled-components';
import { UiStateContext } from '../../context/UiStateContext';
import BranchMapMarker from '../../images/maps/marker-branch.png';
import { Kfh_AreaType, Kfh_PropertyTypeInterface, Kfh_RegionType } from '../../types/graphqlTypes';
import { LOCATION_URLS, PROPERTY_URLS } from '../../urls';
import { Button } from '../Button';
import { ExpanderPanel } from '../ExpanderPanel';
import { Gallery } from '../Gallery';
import HtmlContent from '../Html';
import { Fullscreen } from '../icon/icons/Fullscreen';
import { IconLink } from '../IconLink';
import { Img } from '../Img';
import { MainAndSide } from '../layout/MainAndSide';
import { LinkList, StyledLinkList } from '../LinkList';
import { MapMarker } from '../Map';
import { MapBlade } from '../MapBlade';
import { PageBackButton } from '../PageBackButton';
import { Panel } from '../Panel';
import { PillButtonGroup } from '../PillButtonGroup';
import { RequestAValuationBlade } from '../properties/RequestAValuationBlade';
import { Section } from '../Section';
import { Spacer } from '../Spacer';
import { Typography } from '../Typography';
import { AverageChange } from './AverageChange';
import { LocationActionSection } from './LocationActionSection';
import { RecentPropertiesCarousel } from '../properties/RecentPropertiesCarousel';
import { ExperimentsContext } from '../../context/ExperimentsContext';
import { createMarkup } from '../../utils';
import { Divider } from '../Divider';
import { Container } from '../Container';

interface LocationDetailProps {
  location: Kfh_AreaType | Kfh_RegionType;
  rentalProperties: Kfh_PropertyTypeInterface[];
  resaleProperties: Kfh_PropertyTypeInterface[];
}

const StyledHeader = styled.div`
  ${({ theme: { space, borders } }) => css`
    padding-bottom: ${space.lg};
    border-bottom: ${borders.default};
  `};
`;

const StyledLead = styled(Typography)`
  ${({ theme: { space } }) => css`
    margin: ${space.sm} 0;
  `};
`;

const StyledQuickLinks = styled.div`
  ${({ theme: { space } }) => css`
    padding-top: ${space.lg};
    display: flex;
    flex-direction: column;
    gap: ${space.lg};
  `};
`;

const StyledPropertyLinks = styled.div`
  ${({ theme: { space, media, colors } }) => css`
    display: flex;
    flex-direction: column;
    gap: ${space.sm};

    // Tablet > styles

    @media (min-width: ${media.md}) {
      flex-direction: row;
      gap: ${space.lg};
    }
  `};
`;

const StyledBranch = styled.div`
  ${({ theme: { space, colors } }) => css`
    margin-top: ${space.md};

    a {
      display: flex;

      &:hover {
        color: ${colors.brand.default};
      }

      &[href^='mailto:'] {
        text-decoration: underline;
      }
    }

    & + & {
      margin-top: ${space.lg};
    }
  `};
`;

const StyledBranchPropertyLink = styled(IconLink)`
  ${({ theme: { space } }) => css`
    margin-top: ${space.md};
  `};
`;

const StyledSide = styled.aside`
  ${({ theme: { space, media } }) => css`
    display: grid;
    gap: ${space.md};

    @media (min-width: ${media.md}) {
      grid-template-columns: repeat(2, 1fr);
      gap: ${space.lg};
    }

    @media (min-width: ${media.xl}) {
      display: flex;
      flex-direction: column;
    }
  `};
`;

const StyledPanel = styled(Panel)`
  ${({ theme: { media } }) => css`
    @media (min-width: ${media.xl}) {
      display: none;
    }
  `};
`;

export const splitHtmlAtNthTag = (html: string, targetElement = 'h3', position = 2) => {
  if (!html || typeof html !== 'string') return { before: '', after: '' };

  const regex = new RegExp(`(<${targetElement}[^>]*>)`, 'gi');
  let match;
  let count = 0;
  let splitIndex = -1;

  while ((match = regex.exec(html)) !== null) {
    count++;
    if (count === position) {
      splitIndex = match.index;
      break;
    }
  }

  if (splitIndex === -1) {
    // If the target element doesn't appear enough times, return full content in 'before'
    return { before: html, after: '' };
  }

  return {
    before: html.slice(0, splitIndex),
    after: html.slice(splitIndex),
  };
};

interface ServiceBannerProps {
  isMobile?: boolean;
}

interface ServiceBannerSingleActionProps {
  isMobile?: boolean;
}

const StyledServiceBanner = styled(Panel)<ServiceBannerProps>`
  ${({ theme: { space, media, maxMedia }, isMobile }) => css`
    a {
      color: white;
    }

    @media (max-width: ${maxMedia.lg}) {
      display: ${isMobile ? 'block' : 'none'};
    }

    @media (min-width: ${media.lg}) {
      display: ${isMobile ? 'none' : 'block'};
    }
  `};
`;

const ServiceBanner: FC<ServiceBannerProps> = ({ isMobile }) => (
  <StyledServiceBanner
    backgroundColor="brand"
    heading="Specialist services at every stage of your property journey"
    isMobile={isMobile}
  >
    <LinkList
      items={[
        {
          link: {
            url: '/services/financial-services/',
            name: 'Mortgages and Insurance',
          },
        },
        {
          link: {
            url: '/services/property-solicitors/',
            name: 'Property Solicitors',
          },
        },
        {
          link: {
            url: '/services/surveying-and-professional-services/',
            name: 'Survey and Professional Services',
          },
        },
        {
          link: {
            url: '/financial-services/',
            name: 'View all property services',
          },
        },
      ]}
    />
  </StyledServiceBanner>
);

const ServiceBannerSingleAction: FC<ServiceBannerSingleActionProps> = ({ isMobile }) => (
  <StyledServiceBanner
    backgroundColor="brand"
    heading="We can help you with all your property needs"
    introCopy="Our extensive range of additional property services are designed to assist you at every stage of your property journey."
    buttonOne={{
      label: 'View all property services ',
      variant: 'outlineReversed',
      url: '/financial-services/',
      arrowRight: true,
    }}
    isMobile={isMobile}
  />
);

const iconComponent = BranchMapMarker;

const LocationDetail: FC<LocationDetailProps> = ({ location, rentalProperties, resaleProperties }) => {
  const [isDesktopScreen, setIsWideScreen] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsWideScreen(window.innerWidth > 1199);
    };

    // Set initial value
    handleResize();

    // Listen for window resize events
    window.addEventListener('resize', handleResize);

    // Clean up event listener on component unmount
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const { experiments } = useContext(ExperimentsContext);

  const requestAValuationTriggerEl = useRef() as React.MutableRefObject<HTMLButtonElement>;

  const { branchList } = useStaticQuery(graphql`
    query branchesQueryLoc {
      branchList: allKfhBranchType(sort: { name: ASC }) {
        nodes {
          ...BranchListingDetails
        }
      }
    }
  `);

  const branches = useMemo(
    () =>
      branchList.nodes.reduce((previousValue, currentValue) => {
        if (
          currentValue.isLettings &&
          currentValue.salesLat !== currentValue.lettingsLat &&
          currentValue.salesLon !== currentValue.lettingsLon
        ) {
          const lettingsBranch = {
            ...currentValue,
          };
          delete lettingsBranch.isSales;

          previousValue.push(lettingsBranch);

          const salesBranch = {
            ...currentValue,
          };
          delete salesBranch.isLettings;

          previousValue.push(salesBranch);
        } else {
          previousValue.push(currentValue);
        }
        return previousValue;
      }, []),
    [branchList]
  );

  const mapMarkers: MapMarker[] = useMemo(() => {
    const handleOpenBlade = (singleBranch) => {
      navigate(singleBranch.relativeUrl as string);
    };

    return branches
      .map((singleBranch) => {
        // Stop marker being generated if no lat/long
        if (
          !singleBranch.salesLat ||
          !singleBranch.salesLon ||
          !singleBranch.lettingsLat ||
          !singleBranch.lettingsLon
        ) {
          return;
        }

        return {
          iconComponent,
          id: singleBranch.id,
          position: singleBranch.isLettings
            ? { lat: singleBranch.lettingsLat, lng: singleBranch.lettingsLon }
            : { lat: singleBranch.salesLat, lng: singleBranch.salesLon },
          onClick: () => handleOpenBlade(singleBranch),
        };
      })
      .filter((marker) => !!marker);
  }, [branches]);

  const { setActiveBlade, setActiveBladeTriggerEl } = useContext(UiStateContext);
  const mapBladeTriggerEl = useRef() as React.MutableRefObject<HTMLButtonElement>;
  if (!location) {
    return <>No location</>;
  }

  // shared properties between location and region

  const { name, relativeUrl, mediaSet, seoPages, shortDescription } = location;
  const seo = seoPages.find((seoPage) => seoPage.remoteTypeName === 'KfhSeoAreaGuideType');

  // Specific properties for each type

  const areas = 'relatedAreas' in location ? location?.relatedAreas?.edges : undefined;
  const branch = 'branch' in location ? location?.branch : undefined;
  const region = 'region' in location ? location?.region : undefined;

  const boroughs = location?.relatedBoroughs?.edges;
  const soldDataAverages = location?.locationSoldData?.soldData?.monthlyStatistics;

  const soldData = soldDataAverages ? soldDataAverages[11] : null;

  // Categorisation

  const isArea = !!region;
  const isRegion = !region;

  // Blades

  const handleEnlargeMap = () => {
    setActiveBlade('map');
    setActiveBladeTriggerEl(mapBladeTriggerEl);
  };
  const defaultMultisearch = location.artirixMultiSearch
    ? {
        name: location.artirixDisplayName,
        multiSearch: location.artirixMultiSearch,
      }
    : {};

  const mapCenter = {
    lat: location.latitude,
    lng: location.longitude,
  };

  // Handle Blades
  const handleOpenBladeValuation = (bladeId, triggerEl) => {
    setActiveBlade(bladeId);
    setActiveBladeTriggerEl(triggerEl);
  };

  // Guide guide - split into two parts to inject banner

  const { before: guideContentBefore, after: guideContentAfter } = splitHtmlAtNthTag(location.guide, 'h3', 2);

  return (
    <>
      {/* Back button */}

      <PageBackButton
        url={isArea ? region.relativeUrl : `/${LOCATION_URLS.areaInformation}`}
        label={isArea ? region.name : 'all London Area Information'}
      />

      {/* Gallery */}

      {mediaSet?.media && mediaSet?.media.length > 0 ? (
        <Section contained>
          <Gallery>
            {mediaSet?.media
              .filter(({ media }) => !!media)
              .map(({ media }, index) => (
                <Img src={media} alt={media?.alternativeText || name} key={media?.id || index} />
              ))}
          </Gallery>
        </Section>
      ) : (
        ''
      )}

      {/* Main and sidebar content */}

      <Section contained>
        <MainAndSide>
          {/* Main column */}
          <Spacer responsive spacing="half">
            {/* Heading and Sold data summary */}
            <StyledHeader>
              <Typography component="h1" variant="displaySmall">
                {name}
              </Typography>
              <StyledLead component="p" variant="lead">
                Average sold price last 12 months
              </StyledLead>
              <AverageChange
                average={soldData?.average}
                direction={soldData?.direction}
                percentageChange={soldData?.percentageChange}
                useCurrency
              />

              {/* Quick links */}
              <StyledQuickLinks>
                {/* Sold/ Council tax buttons */}

                <PillButtonGroup>
                  <Button url={`${relativeUrl}${LOCATION_URLS.soldData}`} variant="pill">
                    Sold data
                  </Button>
                  <Button url={`${relativeUrl}${LOCATION_URLS.councilTax}`} variant="pill">
                    Council tax
                  </Button>
                  {isArea && (
                    <>
                      <Button url={`${relativeUrl}${LOCATION_URLS.schools}`} variant="pill">
                        Schools
                      </Button>
                      <Button url={`${relativeUrl}${LOCATION_URLS.transport}`} variant="pill">
                        Transport
                      </Button>
                    </>
                  )}
                </PillButtonGroup>

                {/* For sale / For rent links */}

                {relativeUrl ? (
                  <StyledPropertyLinks>
                    <Button
                      arrowRight
                      url={`${relativeUrl}${PROPERTY_URLS.propertiesForSale}`}
                      label="Properties for sale"
                    />

                    <Button
                      arrowRight
                      url={`${relativeUrl}${PROPERTY_URLS.propertiesForRent}`}
                      label="Properties to rent"
                    />
                  </StyledPropertyLinks>
                ) : (
                  ''
                )}
              </StyledQuickLinks>
            </StyledHeader>
            {experiments?.KFH24006 === 'variation' && !isDesktopScreen && (
              <StyledPanel
                heading="Need a property valuation?"
                backgroundColor="brand"
                buttonOne={{
                  label: 'Arrange a valuation',
                  onClick: () => handleOpenBladeValuation('propertyRequestAValuation', requestAValuationTriggerEl),
                  variant: 'outlineReversed',
                  ref: requestAValuationTriggerEl,
                }}
              >
                <Typography component="p" variant="bodyLarge">
                  Our local experts will provide a free, no-obligation valuation.
                </Typography>
              </StyledPanel>
            )}
            {/* Guide content */}

            {location?.guide ? (
              <Spacer>
                <HtmlContent>{guideContentBefore}</HtmlContent>
                {experiments?.KFH25006 === 'variation' && <ServiceBanner isMobile />}
                <HtmlContent>{guideContentAfter}</HtmlContent>
              </Spacer>
            ) : (
              // No guide available
              <Spacer responsive spacing="half">
                <Typography component="p" variant="lead">
                  We currently do not have a guide for this area. However, you can view other local area information
                  here:
                </Typography>
                <StyledLinkList>
                  <li>
                    <IconLink label="Sold data" url={`${relativeUrl}${LOCATION_URLS.soldData}`} arrowLeft />
                  </li>
                  <li>
                    <IconLink
                      label="Council tax breakdown"
                      url={`${relativeUrl}${LOCATION_URLS.councilTax}`}
                      arrowLeft
                    />
                  </li>
                  <li>
                    <IconLink label="Schools in this area" url={`${relativeUrl}${LOCATION_URLS.schools}`} arrowLeft />
                  </li>
                  <li>
                    <IconLink label="Transport links" url={`${relativeUrl}${LOCATION_URLS.transport}`} arrowLeft />
                  </li>
                  {branch && (
                    <li>
                      <IconLink label={`KFH ${branch.name}`} url={branch.relativeUrl} arrowLeft />
                    </li>
                  )}
                </StyledLinkList>

                {areas && areas.length > 0 ? (
                  <>
                    <Typography component="h3" variant="titleSmall">
                      Nearest available guides
                    </Typography>
                    <StyledLinkList>
                      {areas.map((area) => (
                        <li key={area?.node?.id}>
                          {area?.node?.relativeUrl && (
                            <IconLink label={area?.node.name} url={area?.node.relativeUrl} arrowLeft />
                          )}
                        </li>
                      ))}
                    </StyledLinkList>
                  </>
                ) : (
                  ''
                )}

                {/* Services banner */}
              </Spacer>
            )}
          </Spacer>

          {/* Sidebar content */}

          <StyledSide>
            {/* Nearest Branch */}

            {branch && (
              <Panel
                border
                heading="Nearest KFH branch"
                buttonOne={{
                  label: 'Branch details',
                  url: `${branch.relativeUrl}`,
                  variant: 'outline',
                }}
              >
                <Typography component="h4" variant="titleSmall">
                  KFH {branch.name}
                </Typography>
                <StyledBranch>
                  <Typography component="h5" variant="titleSmall">
                    Sales
                  </Typography>
                  <a href={`tel:${branch.businessProfile?.salesContactDetails?.telephone?.replace(/\s/g, '')}`}>
                    {branch.businessProfile?.salesContactDetails?.telephone}
                  </a>
                  <a href={`mailto:${branch.businessProfile?.salesContactDetails?.email}`}>
                    {branch.businessProfile?.salesContactDetails?.email}
                  </a>
                </StyledBranch>
                <StyledBranch>
                  <Typography component="h5" variant="titleSmall">
                    Lettings
                  </Typography>
                  <a href={`tel:${branch.businessProfile?.lettingsContactDetails?.telephone?.replace(/\s/g, '')}`}>
                    {branch.businessProfile?.lettingsContactDetails?.telephone}
                  </a>
                  <a href={`mailto:${branch.businessProfile?.lettingsContactDetails?.email}`}>
                    {branch.businessProfile?.lettingsContactDetails?.email}
                  </a>
                </StyledBranch>
              </Panel>
            )}

            {/* Services banner */}

            {isArea && experiments?.KFH25006 === 'variation' ? <ServiceBannerSingleAction isMobile /> : ''}

            {/* Location summary */}
            {isArea ? (
              <>
                {experiments?.KFH24006 === 'variation' &&
                  (isDesktopScreen ? (
                    <Panel
                      heading="Need a property valuation?"
                      backgroundColor="brand"
                      buttonOne={{
                        label: 'Arrange a valuation',
                        onClick: () =>
                          handleOpenBladeValuation('propertyRequestAValuation', requestAValuationTriggerEl),
                        variant: 'outlineReversed',
                        ref: requestAValuationTriggerEl,
                      }}
                    >
                      <Typography component="p" variant="bodyLarge">
                        Our local experts will provide a free, no-obligation valuation.
                      </Typography>
                    </Panel>
                  ) : (
                    ''
                  ))}
                <Panel
                  border
                  heading="Location"
                  buttonOne={{
                    label: 'Schools in this area',
                    url: `${relativeUrl}${LOCATION_URLS.schools}`,
                    variant: 'outline',
                    arrowRight: true,
                  }}
                  buttonTwo={{
                    label: 'Transport links',
                    url: `${relativeUrl}${LOCATION_URLS.transport}`,
                    variant: 'outline',
                    arrowRight: true,
                  }}
                >
                  {location?.staticMap && (
                    <button type="button" onClick={handleEnlargeMap} ref={mapBladeTriggerEl}>
                      <Typography component="span" variant="titleSmall" visuallyHidden>
                        View map
                      </Typography>
                      <Img src={location.staticMap} />
                    </button>
                  )}
                </Panel>
              </>
            ) : (
              <>
                <Panel
                  border
                  heading="Location"
                  buttonOne={{
                    label: 'Enlarge Map',
                    onClick: () => handleEnlargeMap(),
                    variant: 'outline',
                    iconRight: <Fullscreen size="small" />,
                    // as: 'button',
                  }}
                >
                  {location?.staticMap && (
                    <button type="button" onClick={handleEnlargeMap} ref={mapBladeTriggerEl}>
                      <Img src={location.staticMap} />
                    </button>
                  )}
                  {region && (
                    <>
                      <Link to={`${relativeUrl}${LOCATION_URLS.schools}`}>Schools in this area</Link>
                      <Link to={`${relativeUrl}${LOCATION_URLS.transport}`}>Transport links</Link>
                    </>
                  )}
                </Panel>
                {experiments?.KFH24010 === 'variation' && <ServiceBanner />}
                {experiments?.KFH24006 === 'variation' &&
                  (isDesktopScreen ? (
                    <Panel
                      heading="Need a property valuation?"
                      backgroundColor="brand"
                      buttonOne={{
                        label: 'Arrange a valuation',
                        onClick: () =>
                          handleOpenBladeValuation('propertyRequestAValuation', requestAValuationTriggerEl),
                        variant: 'outlineReversed',
                        ref: requestAValuationTriggerEl,
                      }}
                    >
                      <Typography component="p" variant="bodyLarge">
                        Our local experts will provide a free, no-obligation valuation.
                      </Typography>
                    </Panel>
                  ) : (
                    ''
                  ))}
              </>
            )}
            {mapMarkers?.length >= 1 && (
              <MapBlade
                mapMarkers={mapMarkers}
                mapStyle="regions"
                ignoreMarkersFromBounds
                center={mapCenter}
                polygon={location.coordinates}
                showPolygon={false}
              />
            )}

            {/* Council tax */}

            <Panel
              border
              heading="Council tax"
              buttonOne={{
                label: 'Council tax breakdown',
                url: `${relativeUrl}${LOCATION_URLS.councilTax}`,
                variant: 'outline',
                arrowRight: true,
              }}
            >
              <HtmlContent>
                <p>{name} covers the boroughs of:</p>
                <ul>{boroughs && boroughs.map((borough) => <li key={borough?.node?.id}>{borough?.node?.name}</li>)}</ul>
              </HtmlContent>
            </Panel>
          </StyledSide>
        </MainAndSide>
      </Section>

      {areas && isRegion ? (
        <Section contained>
          <ExpanderPanel title={name} open>
            <Spacer>
              {shortDescription && <p>{shortDescription}</p>}
              <StyledLinkList grid>
                {areas.map((area) => (
                  <li key={area?.node?.id}>
                    {area?.node?.relativeUrl && (
                      <IconLink label={area?.node.name} url={area?.node.relativeUrl} arrowLeft />
                    )}
                  </li>
                ))}
              </StyledLinkList>
            </Spacer>
          </ExpanderPanel>
        </Section>
      ) : (
        ''
      )}
      {/* Recent Properties */}

      <RecentPropertiesCarousel
        rentalProperties={rentalProperties}
        resaleProperties={resaleProperties}
        searchSaleUrl={`${relativeUrl}${PROPERTY_URLS.propertiesForSale}`}
        searchRentUrl={`${relativeUrl}${PROPERTY_URLS.propertiesForRent}`}
        searchType={location.name}
      />

      {!isArea && experiments?.KFH25006 === 'variation' ? (
        <Container>
          <ServiceBannerSingleAction isMobile />
        </Container>
      ) : (
        ''
      )}

      {/* Actions */}
      <LocationActionSection defaultMultisearch={defaultMultisearch} />

      {/* Valuation blade*/}

      <RequestAValuationBlade />
    </>
  );
};

export default LocationDetail;
